import permissionsRequired from '~/singlePageApp/illustrations/permissions-required.svg';
import { match } from 'ts-pattern';
import { EventMessageCard } from '~/singlePageApp/screens/LiveStreamScreen/Broadcast/components/shared/EventMessageCard';
import { Button } from '@wistia/vhs';
import { includesString } from '~/utilities/util';
import { useLoadFeatureFlags } from '~/singlePageApp/hooks/useLoadFeatureFlags';
import { externalLinks } from '~/externalLinks';
import type { JSX } from 'react';

// The error names come from the 100ms Error Factory class here:
// https://github.com/100mslive/100ms-react-sdk/blob/main/packages/react-sdk/src/errors/ErrorFactory.ts
// - Burns
type NonBlockingHandledHMSErrorNames =
  | 'CantAccessCaptureDevice'
  | 'DeviceInUse'
  | 'DeviceNotAvailable'
  | 'NoAudioDetected'
  | 'SystemDeniedPermission'
  | 'Track does not have any data';
interface NonBlockingHandledError extends Error {
  name: NonBlockingHandledHMSErrorNames;
}

export const NON_BLOCKING_HANDLED_ERRORS = [
  'CantAccessCaptureDevice',
  'SystemDeniedPermission',
  'Track does not have any data',
  'DeviceInUse',
  'NoAudioDetected',
  'DeviceNotAvailable',
] satisfies NonBlockingHandledHMSErrorNames[];

export const isNonBlockingHandledError = (error: Error): error is NonBlockingHandledError => {
  return includesString(NON_BLOCKING_HANDLED_ERRORS, error.name);
};

type BlockingHandledHMSErrorMessage =
  | 'Cannot return null for non-nullable field AnonymousQuery.audienceLiveStreamEvent'
  | 'Cross account access attempt';

export interface BlockingHandledError extends Error {
  message: BlockingHandledHMSErrorMessage;
  currentAccount?: string;
  targetAccount?: string;
}

export const BLOCKING_HANDLED_ERRORS: BlockingHandledHMSErrorMessage[] = [
  'Cannot return null for non-nullable field AnonymousQuery.audienceLiveStreamEvent',
  'Cross account access attempt',
];

export const isBlockingHandledError = (error: Error): error is BlockingHandledError =>
  includesString(BLOCKING_HANDLED_ERRORS, error.message);

export const isHandledError = (error: Error): boolean => {
  return isNonBlockingHandledError(error) || isBlockingHandledError(error);
};

/**
 * The component that handles errors and displays the appropriate message to the
 * user
 *
 * @param error - The error object
 */
export const LiveErrorCard = ({ blockingError }: { blockingError: Error }): JSX.Element => {
  const { flags } = useLoadFeatureFlags(['flag_vma_brand_refresh']);

  const isBrandRefreshEnabled = flags.flag_vma_brand_refresh.enabled;

  const {
    headline,
    illustration,
    message,
    subMessage,
    shouldShowButtonRow,
    buttons: customButtons,
  } = match(blockingError)
    .when(isBlockingHandledError, (blockingHandledError) =>
      match(blockingHandledError)
        .with(
          {
            message:
              'Cannot return null for non-nullable field AnonymousQuery.audienceLiveStreamEvent',
          },
          () => ({
            headline: 'Sorry, this event is no longer available',
            illustration: null,
            message: 'This event has been either cancelled or deleted by the host.',
            subMessage: '',
            shouldShowButtonRow: false,
            buttons: [],
          }),
        )
        .with(
          {
            message: 'Cross account access attempt',
          },
          (error) => ({
            headline: 'Account Mismatch Detected',
            illustration: null,
            message: `You're currently logged into the Wistia account for ${error.currentAccount}.wistia.com, but you're trying to access the host view for ${error.targetAccount}.wistia.com.`,
            subMessage:
              "To proceed, please log out and try again. You'll be prompted to log into the correct account.",
            shouldShowButtonRow: true,
            buttons: [
              <Button
                key="sign-out"
                label={`Logout ${error.currentAccount}`}
                onClick={() => {
                  const redirectUrl = encodeURIComponent(window.location.href);
                  window.location.href = `/logout?redirect=${redirectUrl}`;
                }}
                variant={isBrandRefreshEnabled ? 'primary' : 'secondary'}
              />,
            ],
          }),
        )
        .exhaustive(),
    )
    .otherwise(() => ({
      headline: 'Oops, something went wrong',
      illustration: permissionsRequired,
      message: 'We cannot broadcast because something went wrong.',
      subMessage: 'Reload your browser to try again.',
      shouldShowButtonRow: true,
      buttons: [
        <Button
          key="read-troubleshooting"
          label="Read troubleshooting guides"
          onClick={() => window.open(externalLinks.recordCameraMicScreenSharing, '_blank')}
          variant="secondary"
        />,
        <Button
          key="reload"
          iconName="redo"
          label="Reload page"
          onClick={() => window.location.reload()}
          variant={isBrandRefreshEnabled ? 'primary' : 'secondary'}
        />,
      ],
    }));

  return (
    <EventMessageCard
      buttons={customButtons}
      illustration={illustration}
      headline={headline}
      message={message}
      subMessage={subMessage}
      shouldShowButtonRow={shouldShowButtonRow}
    />
  );
};
